import { useState } from "react";
import type { ChangePasswordFormTypes } from "../../types/admin/admin.form";
import { useAppDispatch } from "../../redux/hooks";
import { changePassword } from "../../redux/slices/adminSlice";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../utils/errorHandler";
import { useNavigate } from "react-router-dom";
import buttonLoadingAnimation from "../../animations/buttonLoading.json";
import Lottie from "lottie-react";

export interface ChangePasswordProps {
  handleClose: () => void;
}

export const ChangePassword = ({ handleClose }: ChangePasswordProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<ChangePasswordFormTypes>({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const [loading, setLoading] = useState<boolean>(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.newPassword !== formData.confirmPassword) {
      toast.error("Mật khẩu mới không khớp");
      return;
    }
    if (formData.newPassword.length < 8) {
      toast.error("Mật khẩu mới tối thiểu 8 ký tự");
      return;
    }
    setLoading(true);
    try {
      await dispatch(
        changePassword({
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword,
        }),
      ).unwrap();
      toast.success("Đổi mật khẩu thành công. Vui lòng đăng nhập lại.");
      navigate("/login", { replace: true });
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-3 z-50">
      <div className="p-5 bg-white rounded-lg w-full max-w-md space-y-2">
        <div className="flex items-center justify-center">
          <p className="text-lg font-semibold text-gray-700">Đổi mật khẩu</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Mật khẩu hiện tại
            </label>
            <input
              type="password"
              name="currentPassword"
              value={formData.currentPassword}
              onChange={handleChange}
              required
              className="p-2 focus:outline-none focus:border-blue-500 transition-colors duration-300 border border-gray-200 rounded-xl w-full placeholder:text-sm placeholder:text-gray-400"
              placeholder="Nhập mật khẩu hiện tại"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Mật khẩu mới
            </label>
            <input
              type="password"
              name="newPassword"
              value={formData.newPassword}
              onChange={handleChange}
              required
              className="p-2 focus:outline-none focus:border-blue-500 transition-colors duration-300 border border-gray-200 rounded-xl w-full placeholder:text-sm placeholder:text-gray-400"
              placeholder="Tối thiểu 8 ký tự"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Xác nhận mật khẩu mới
            </label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              className="p-2 focus:outline-none focus:border-blue-500 transition-colors duration-300 border border-gray-200 rounded-xl w-full placeholder:text-sm placeholder:text-gray-400"
              placeholder="Nhập lại mật khẩu mới"
            />
          </div>
          <div className="flex items-center justify-between pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="p-2 text-white text-sm bg-gray-500 hover:bg-gray-600 rounded-lg cursor-pointer"
            >
              Hủy
            </button>
            {loading ? (
              <Lottie
                animationData={buttonLoadingAnimation}
                loop={true}
                className="h-10 w-20"
              />
            ) : (
              <button
                type="submit"
                className="p-2 text-white text-sm bg-green-500 hover:bg-green-600 rounded-lg cursor-pointer"
              >
                Đổi mật khẩu
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};
